import React, {Component} from "react"
import PropTypes from "prop-types"

import History from "@irati/utils/history"
import {H2, Body} from "@irati/ui/src/Typography"
import {Hr} from "@irati/ui/src/Layout"
import Flex from "@irati/ui/src/Flex"
import {FormattedMessage, injectIntl, InjectIntl} from "@irati/utils/lang"
import workosService from "@irati/workos/services/workos"
import authentication from "@irati/auth"
import adminTracking from "@irati-tracking/admin"
import Loading from "@irati/ui/src/Loading"
import businessService from "@irati/identity/services/business"
import {
  AzureLogo,
  BambooHrLogo,
  BobLogo,
  GoogleLogo,
  MicrosoftLogo,
  MicrosoftTeamsLogo,
  OktaLogo,
  SamlLogo,
  WorkDayLogo
} from "@irati/ui/src/Logos"
import {getOAuthSettings} from "@irati/sso/services/sso"
import {OAuthProvider, OnboardingStep} from "@irati/types"
import Link from "@irati/ui/src/Link"
import theme from "@irati/ui/src/global/theme"
import adminReportingService from "@irati/adminReporting/services/adminReporting"
import devToggles from "@irati/dev-toggles"

import IntegrationCard from "../../../components/Settings/Integration/Card"
import Help from "../../../components/Settings/Integration/Help"
import {IFrameWrapper, IFrame, TextWrapper} from "../styled"
import {SETTINGS_LOGIN_INTEGRATION, SETTINGS_LOGIN_INTEGRATION_SSO, SETTINGS_LOGIN_INTEGRATION_DSYNC} from "../../../routes/routeConfig"
import FirstMonthFreeOffer from "../../../components/Settings/Integration/DirectorySync/HRIS/FirstMonthFreeOffer"

const SSO_LEARN_MORE = "SSO_learnMore"
const DSYNC_LEARN_MORE = "DSYNC_learnMore"

interface Props extends InjectIntl {}

interface State {
  load: Promise<void> | null
  organisationCreated: boolean
  singleSignOnEnabled: boolean
  directorySyncEnabled: boolean
  msTeamsEnabled: boolean
  createOrganisationSubmit: Promise<any> | null
  singleSignOnLogos: any[]
  directorySyncLogos: any[]
  hasBusinessRestrictions: boolean
}

class Integrations extends Component<Props, State> {
  state: State = {
    load: null,
    organisationCreated: false,
    singleSignOnEnabled: false,
    directorySyncEnabled: false,
    msTeamsEnabled: false,
    createOrganisationSubmit: null,
    singleSignOnLogos: [],
    directorySyncLogos: [],
    hasBusinessRestrictions: false
  }

  static contextTypes = {
    appPath: PropTypes.string
  }

  componentDidMount() {
    const load = workosService.getOrganisation(authentication.business().uuid).then((organisation) => {
      this.setState({
        organisationCreated: !!organisation?.organisationId,
        singleSignOnEnabled: !!organisation?.connectionId,
        directorySyncEnabled: !!organisation?.directoryId
      })
      if (!organisation?.connectionId) {
        getOAuthSettings(authentication.business().uuid, {fetchPolicy: "network-only"}).then((data) => {
          if (data.provider !== OAuthProvider.Unknown) {
            this.setState({singleSignOnEnabled: true})
          }
        })
      }
      adminReportingService
        .getOnboardingStep({businessId: authentication.business().uuid, step: OnboardingStep.MicrosoftTeamsEnabled})
        .then(({complete}) => {
          this.setState({msTeamsEnabled: complete})
        })
      businessService.getBusinessRestrictions(authentication.business().uuid, {fetchPolicy: "network-only"}).then(({hasMaxUserLimit}) => {
        if (hasMaxUserLimit) {
          this.setState({
            hasBusinessRestrictions: hasMaxUserLimit
          })
        }
      })
    })

    this.setState({load})
    this.setState({
      singleSignOnLogos: [
        <OktaLogo id="integrations-logo-okta" />,
        <AzureLogo id="integrations-logo-azure" />,
        <MicrosoftLogo id="integrations-logo-microsoft" />,
        <GoogleLogo id="integrations-logo-google" />,
        <SamlLogo id="integrations-logo-saml" />
      ]
    })
    this.setState({
      directorySyncLogos: [
        <OktaLogo id="integrations-logo-okta" />,
        <AzureLogo id="integrations-logo-azure" />,
        <BambooHrLogo id="integrations-logo-bamboohr" />,
        <BobLogo id="integrations-logo-bob" />,
        <WorkDayLogo id="integrations-logo-workday" />
      ]
    })
  }

  startIntegration = () => {
    if (!this.state.organisationCreated) {
      const createOrganisationSubmit = workosService.createOrganisation(authentication.business().uuid).then(() => {
        this.setState({organisationCreated: true})
      })
      this.setState({createOrganisationSubmit})
    }
  }

  toggleSSO = () => {
    adminTracking.startIntegrationsSSOJourney(SSO_LEARN_MORE)
    this.startIntegration()
    History.push(
      {
        pathname: `${SETTINGS_LOGIN_INTEGRATION_SSO}`
      },
      this.context
    )
  }

  togglePeopleDirectory = () => {
    adminTracking.startIntegrationsDSYNCJourney(DSYNC_LEARN_MORE)
    this.startIntegration()
    History.push(
      {
        pathname: `${SETTINGS_LOGIN_INTEGRATION_DSYNC}`
      },
      this.context
    )
  }

  onClickBack = () => {
    History.push(
      {
        pathname: `${SETTINGS_LOGIN_INTEGRATION}`
      },
      this.context
    )
  }

  render() {
    const {formatMessage} = this.props
    const {
      load,
      organisationCreated,
      singleSignOnEnabled,
      directorySyncEnabled,
      msTeamsEnabled,
      createOrganisationSubmit,
      singleSignOnLogos,
      directorySyncLogos,
      hasBusinessRestrictions
    } = this.state

    return (
      <Loading load={load}>
        <Flex justifyContent="space-between">
          <TextWrapper>
            <H2 marginBottom={4}>
              <FormattedMessage id="admin.containers.integrations.header.title" />
            </H2>
            <Body marginBottom={2}>
              <FormattedMessage id="admin.containers.integrations.header.text1" />
            </Body>
            <Body marginBottom={2}>
              <FormattedMessage id="admin.containers.integrations.header.text2" />
            </Body>
          </TextWrapper>
          <IFrameWrapper>
            <IFrame
              title="integrationsVideo"
              src={formatMessage({id: "admin.containers.integrations.video"})}
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              mozallowfullscreen
              webkitallowfullscreen
            />
          </IFrameWrapper>
        </Flex>

        <Hr />
        <Flex direction={hasBusinessRestrictions ? "row" : "column"} gap={theme.spacing.four}>
          <Flex gap={theme.spacing.four}>
            <IntegrationCard
              enabled={organisationCreated && singleSignOnEnabled}
              purchased={devToggles.showSSOFeatureFlag() ? !!authentication.apps("admin.singleSignOn") : true}
              title="admin.containers.integrations.card.singleSignOn.title"
              text="admin.containers.integrations.card.singleSignOn.text"
              logos={singleSignOnLogos}
              onClick={this.toggleSSO}
              load={createOrganisationSubmit}
            />
            {!hasBusinessRestrictions && (
              <IntegrationCard
                enabled={organisationCreated && directorySyncEnabled}
                purchased={!!authentication.apps("admin.directorySync")}
                title="admin.containers.integrations.card.directorySync.title"
                text="admin.containers.integrations.card.directorySync.text"
                logos={directorySyncLogos}
                onClick={this.togglePeopleDirectory}
                load={createOrganisationSubmit}>
                {!authentication.apps("admin.directorySync") && <FirstMonthFreeOffer />}
              </IntegrationCard>
            )}
          </Flex>
          <Flex width={hasBusinessRestrictions ? "50%" : "none"} marginTop={hasBusinessRestrictions ? 0 : 4}>
            <IntegrationCard
              enabled={msTeamsEnabled}
              purchased
              title="admin.containers.integrations.card.microsoftTeams.title"
              text="admin.containers.integrations.card.microsoftTeams.text"
              textValues={{
                video: (
                  <Link href={formatMessage({id: "admin.containers.integrations.card.microsoftTeams.textLink"})} target="_blank">
                    <FormattedMessage id="admin.containers.integrations.card.microsoftTeams.linkValue" />
                  </Link>
                )
              }}
              logos={[<MicrosoftTeamsLogo src={formatMessage({id: "admin.containers.integrations.card.microsoftTeams.imgLink"})} />]}
              tooltipLabel={{disabled: "admin.containers.integrations.card.microsoftTeams.labelTooltip.disabled"}}
              onClick={() => window.open(formatMessage({id: "admin.containers.integrations.card.microsoftTeams.link"}), "_blank")}
              load={createOrganisationSubmit}
            />
          </Flex>
        </Flex>

        <Help />
      </Loading>
    )
  }
}

export default injectIntl(Integrations)
